import React, { useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import DateRange from "@/components/partials/DateRange"

const OBJECT_TYPES = [
  { value: "part", labelKey: "analytics.scenarioObjectTypePart" },
  { value: "service", labelKey: "analytics.scenarioObjectTypeService" },
  { value: "technician", labelKey: "analytics.scenarioObjectTypeTechnician" },
]

const SCENARIO_TYPES = [
  { value: "demand", labelKey: "analytics.scenarioDemandChange" },
  { value: "price", labelKey: "analytics.scenarioPriceChange" },
  { value: "resource", labelKey: "analytics.scenarioResourceChange" },
]

// Each object type has exactly one applicable scenario:
// parts -> price change, services/categories -> demand change,
// technicians -> resource change.
const SCENARIO_BY_OBJECT_TYPE = {
  part: "price",
  service: "demand",
  technician: "resource",
}

const INITIAL_FILTERS = {
  object_type: "part",
  object_id: "",
  scenario_type: SCENARIO_BY_OBJECT_TYPE.part,
  change_percentage: 0,
  date_from: null,
  date_to: null,
}

function objectLabel(obj) {
  return [obj.name, obj.lastname].filter(Boolean).join(" ") || obj.number || ""
}

function OrdersInTime() {
  const [objectsLoading, setObjectsLoading] = useState(false)
  const [simulating, setSimulating] = useState(false)
  const [objects, setObjects] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const [chartLabels, setChartLabels] = useState([])
  const [histSeries, setHistSeries] = useState([])
  const [foreSeries, setForeSeries] = useState([])
  // simResult holds the raw scenario-analysis response
  const [simResult, setSimResult] = useState(null)

  useEffect(() => {
    setObjects([])
    setFilters((prev) => ({
      ...prev,
      object_id: "",
      scenario_type: SCENARIO_BY_OBJECT_TYPE[prev.object_type],
    }))
    loadObjects(filters.object_type)
  }, [filters.object_type])

  function loadObjects(type) {
    setObjectsLoading(true)
    Api.get(`analytics/scenario-objects?object_type=${type}`)
      .then((res) => {
        setObjects(res.data.objects || [])
        setObjectsLoading(false)
      })
      .catch(() => setObjectsLoading(false))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function handleRunSimulation() {
    setSimulating(true)
    setSimResult(null)

    const params = new URLSearchParams({
      object_type: filters.object_type,
      scenario_type: filters.scenario_type,
      change_percentage: filters.change_percentage,
    })
    if (filters.object_id) params.append("object_id", filters.object_id)
    if (filters.date_from) params.append("date_from", filters.date_from)
    if (filters.date_to) params.append("date_to", filters.date_to)

    // All forecasting (trend + month/day-of-week seasonality) now happens
    // server-side; the component only reads the pre-computed values.
    Api.get(`analytics/scenario-analysis?${params.toString()}`)
      .then((res) => {
        const d = res.data
        setChartLabels(d.chart_data?.labels || [])
        setHistSeries(d.chart_data?.hist_series || [])
        setForeSeries(d.chart_data?.fore_series || [])
        setSimResult(d)
        setSimulating(false)
      })
      .catch(() => setSimulating(false))
  }

  // const baseline = simResult?.current_value ?? 0
  // const forecastValue = simResult?.forecast_value ?? 0
  // const diffPct =
  //   simResult?.difference_percentage != null
  //     ? simResult.difference_percentage.toFixed(1)
  //     : "0.0"
  // const diffPositive = parseFloat(diffPct) >= 0
  // const forecastRevenue = simResult?.forecast_revenue ?? null
  // const requiredTechnicians = simResult?.capacity?.required_technicians ?? null

  // const recommendation = simResult?.recommendation
  //   ? trans(simResult.recommendation.key, simResult.recommendation.params) ||
  //     simResult.recommendation.text
  //   : ""

  const insufficientData = simResult?.insufficient_data === true
  const hasChartData =
    simResult !== null && !insufficientData && chartLabels.length > 0

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>{trans("analytics.ordersInTime")}</h3>
      </div>
      <hr />

      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "flex-end",
          marginBottom: "20px",
        }}
      >
        <div style={filterColStyle}>
          <label>{trans("filters.category")}</label>
          <select
            value={filters.object_type}
            onChange={(e) => handleFilterChange("object_type", e.target.value)}
          >
            {OBJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {trans(t.labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioObject")}</label>
          {objectsLoading ? (
            <Skeleton width={160} height={38} />
          ) : (
            <select
              value={filters.object_id}
              onChange={(e) => handleFilterChange("object_id", e.target.value)}
            >
              <option value="">{trans("random.all")}</option>
              {objects.map((obj) => (
                <option key={obj.id} value={obj.id}>
                  {objectLabel(obj)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioType")}</label>
          <select
            value={filters.scenario_type}
            onChange={(e) =>
              handleFilterChange("scenario_type", e.target.value)
            }
          >
            {SCENARIO_TYPES.filter(
              (t) => t.value === SCENARIO_BY_OBJECT_TYPE[filters.object_type],
            ).map((t) => (
              <option key={t.value} value={t.value}>
                {trans(t.labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.changePercentage")}</label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "38px",
            }}
          >
            <input
              type="range"
              min="-50"
              max="50"
              step="1"
              value={filters.change_percentage}
              onChange={(e) =>
                handleFilterChange("change_percentage", e.target.value)
              }
              style={{ width: "120px", cursor: "pointer", accentColor: "#181F67" }}
            />
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                minWidth: "42px",
                color:
                  filters.change_percentage > 0
                    ? "#3DAA77"
                    : filters.change_percentage < 0
                      ? "#FF718B"
                      : "#888",
              }}
            >
              {filters.change_percentage > 0 ? "+" : ""}
              {filters.change_percentage}%
            </span>
          </div>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioPeriod")}</label>
          <DateRange
            startDate={filters.date_from || ""}
            endDate={filters.date_to || ""}
            onStartDateChange={(date) => handleFilterChange("date_from", date)}
            onEndDateChange={(date) => handleFilterChange("date_to", date)}
          />
        </div>

        <button
          onClick={handleRunSimulation}
          disabled={simulating}
          style={{ height: "40px" }}
          className="button button-primary"
        >
          {simulating ? "..." : trans("analytics.runSimulation")}
        </button>
      </div>

      {/* Chart */}
      {simulating ? (
        <Skeleton height={295} />
      ) : insufficientData ? (
        <div
          className="alert warning"
          style={{ marginBottom: 0, width: "100%" }}
        >
          {trans("analytics.scenarioInsufficientData")}
        </div>
      ) : !hasChartData ? (
        <div className="alert warning" style={{ marginBottom: 0, width: "100%" }}>
          {trans("analytics.scenarioRunPrompt")}
        </div>
      ) : (
        <>
          <LineChart
            xAxis={[{ scaleType: "point", data: chartLabels }]}
            series={[
              {
                id: "sa-hist",
                curve: "natural",
                label: trans("analytics.historicalData"),
                area: true,
                data: histSeries,
                color: "#181F67",
                valueFormatter: (v) => (v == null ? "" : String(Math.round(v))),
              },
              {
                id: "sa-fore",
                curve: "natural",
                label: trans("analytics.forecast"),
                area: true,
                data: foreSeries,
                color: "#181F67",
                valueFormatter: (v) => (v == null ? "" : String(Math.round(v))),
              },
            ]}
            height={295}
            sx={{
              "& .MuiLineElement-series-sa-fore": { strokeDasharray: "6 3" },
              "& .MuiAreaElement-series-sa-hist": { fill: "url(#sa-hist-gradient)" },
              "& .MuiAreaElement-series-sa-fore": { fill: "url(#sa-fore-gradient)" },
              "& .MuiLineElement-root": { strokeWidth: 1.5 },
            }}
            slotProps={{ legend: { hidden: true } }}
          >
            <defs>
              <linearGradient id="sa-hist-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#181F67" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="sa-fore-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#181F67" stopOpacity={0} />
                <stop offset="100%" stopColor="#181F67" stopOpacity={0} />
              </linearGradient>
            </defs>
          </LineChart>

          {/* Results summary */}
          {/* <div
            style={{
              display: "flex",
              gap: "0",
              marginTop: "16px",
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid #eef0f6",
            }}
          >
            <ResultCard
              label={trans("analytics.currentValue")}
              value={Math.round(baseline)}
              color="#181F67"
            />
            <ResultCard
              label={trans("analytics.forecastValue")}
              value={Math.round(forecastValue)}
              color="#3DAA77"
              divider
            />
            <ResultCard
              label={trans("analytics.difference")}
              value={`${diffPositive ? "+" : ""}${diffPct}%`}
              color={diffPositive ? "#3DAA77" : "#FF718B"}
              divider
            />
            {forecastRevenue !== null && (
              <ResultCard
                label={trans("analytics.forecastRevenue")}
                value={Math.round(forecastRevenue)}
                color="#181F67"
                divider
              />
            )}
            {requiredTechnicians !== null && (
              <ResultCard
                label={trans("analytics.requiredTechnicians")}
                value={requiredTechnicians}
                color="#181F67"
                divider
              />
            )}
          </div> */}

          {/* Recommendation */}
          {/* {recommendation && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 16px",
                background: "#f0f3ff",
                borderRadius: "8px",
                borderLeft: "3px solid #181F67",
                fontSize: "13px",
                color: "#333",
                lineHeight: "1.5",
              }}
            >
              {recommendation}
            </div>
          )} */}
        </>
      )}
    </div>
  )
}

// function ResultCard({ label, value, color, divider }) {
//   return (
//     <div
//       style={{
//         flex: 1,
//         padding: "14px 20px",
//         background: "#f8f9fc",
//         borderLeft: divider ? "1px solid #eef0f6" : "none",
//       }}
//     >
//       <div
//         style={{
//           fontSize: "11px",
//           color: "#999",
//           marginBottom: "6px",
//           textTransform: "uppercase",
//           letterSpacing: "0.5px",
//         }}
//       >
//         {label}
//       </div>
//       <div style={{ fontSize: "22px", fontWeight: "700", color }}>{value}</div>
//     </div>
//   )
// }

const filterColStyle = { display: "flex", flexDirection: "column", gap: "6px" }

export default OrdersInTime
